import React from 'react';
import { Sparkles } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { isWithinAILimit, incrementAIUsage } from '../lib/aiUsage';

interface AISuggestionsProps {
  input: string;
  onSuggestionClick: (suggestion: string) => void;
  onClearInput?: () => void;
}

export function AISuggestions({ input, onSuggestionClick, onClearInput }: AISuggestionsProps) {
  const [suggestions, setSuggestions] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [aiLimitReached, setAiLimitReached] = React.useState(false);
  const suggestionsRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        setSuggestions([]); // Clear suggestions when clicking outside
        setError(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getSuggestions = async (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent form submission
    if (!input.trim()) return;
    
    setLoading(true);
    setShowSuggestions(true);
    setError(null);
    setAiLimitReached(false);
    
    try {
      console.log('AISuggestions: Starting to get suggestions for input:', input.trim());
      
      // First check if the user is within their AI usage limit
      console.log('AISuggestions: Checking if user is within AI limit');
      const isWithinLimit = await isWithinAILimit();
      console.log('AISuggestions: Is within limit result:', isWithinLimit);
      
      if (!isWithinLimit) {
        console.log('AISuggestions: User has reached AI limit');
        setAiLimitReached(true);
        setError('You have reached your daily AI suggestions limit');
        setLoading(false);
        return;
      }
      
      // Call the Supabase Edge Function
      console.log('AISuggestions: Calling edge function');
      const { data, error } = await supabase.functions.invoke('get-ai-suggestions', {
        body: { input: input.trim() }
      });

      if (error) {
        console.error('AISuggestions: Edge function error:', error);
        throw new Error(`Edge function error: ${error.message}`);
      }

      console.log('AISuggestions: Edge function response:', data);

      // Process the response
      if (data && data.suggestions && Array.isArray(data.suggestions)) {
        console.log('AISuggestions: Found suggestions array directly:', data.suggestions);
        setSuggestions(data.suggestions);
        
        // Increment AI usage counter on successful suggestions
        console.log('AISuggestions: Incrementing AI usage');
        const incrementResult = await incrementAIUsage();
        console.log('AISuggestions: Increment result:', incrementResult);
        
        // Dispatch a custom event to notify other components to refresh AI usage
        window.dispatchEvent(new CustomEvent('aiUsageUpdated'));
      } else if (data && data.result) {
        try {
          console.log('AISuggestions: Trying to parse result string:', data.result);
          const parsed = JSON.parse(data.result);
          if (Array.isArray(parsed.Result)) {
            console.log('AISuggestions: Found Result array in parsed JSON:', parsed.Result);
            setSuggestions(parsed.Result);
            const incrementResult = await incrementAIUsage();
            console.log('AISuggestions: Increment result:', incrementResult);
            window.dispatchEvent(new CustomEvent('aiUsageUpdated'));
          } else if (Array.isArray(parsed)) {
            console.log('AISuggestions: Found array in parsed JSON:', parsed);
            setSuggestions(parsed);
            const incrementResult = await incrementAIUsage();
            console.log('AISuggestions: Increment result:', incrementResult);
            window.dispatchEvent(new CustomEvent('aiUsageUpdated'));
          } else {
            console.error('AISuggestions: Invalid response format:', parsed);
            throw new Error('Invalid response format');
          }
        } catch (e) {
          console.error('AISuggestions: Failed to parse edge function response:', e);
          throw new Error('Failed to parse suggestions');
        }
      } else if (data && typeof data === 'object') {
        // Try to find any array in the response
        console.log('AISuggestions: Searching for array in response object');
        const possibleSuggestions = Object.values(data).find(val => Array.isArray(val));
        if (possibleSuggestions) {
          console.log('AISuggestions: Found array in response object:', possibleSuggestions);
          setSuggestions(possibleSuggestions);
          const incrementResult = await incrementAIUsage();
          console.log('AISuggestions: Increment result:', incrementResult);
          window.dispatchEvent(new CustomEvent('aiUsageUpdated'));
        } else {
          console.error('AISuggestions: No valid suggestions found in response:', data);
          throw new Error('No valid suggestions found in response');
        }
      } else {
        console.error('AISuggestions: Invalid response from AI service:', data);
        throw new Error('Invalid response from AI service');
      }
    } catch (error) {
      console.error('AISuggestions: Error getting AI suggestions:', error);
      setError(error instanceof Error ? error.message : 'Failed to get suggestions');
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = async (suggestion: string) => {
    console.log('AISuggestions: Suggestion clicked:', suggestion);
    await onSuggestionClick(suggestion);

    // Clear the input field after successfully adding the task
    if (onClearInput) {
      onClearInput();
    }

    // Notify other components to refresh AI usage
    window.dispatchEvent(new CustomEvent('aiUsageUpdated'));

    // Remove the selected suggestion from the list but keep showing the remaining ones
    setSuggestions(prev => {
      const newSuggestions = prev.filter(s => s !== suggestion);
      // Only hide suggestions if there are none left
      if (newSuggestions.length === 0) {
        setShowSuggestions(false);
      }
      return newSuggestions;
    });
  };

  return (
    <div className="relative ai-suggestions-container" ref={suggestionsRef}>
      <button
        onClick={getSuggestions}
        disabled={loading}
        className="flex items-center justify-center text-indigo-600 hover:text-indigo-700 focus:outline-none disabled:opacity-50 h-full px-3"
        title="Get AI suggestions"
        type="button" // Explicitly set type to button to prevent form submission
      >
        <Sparkles className="h-5 w-5" />
      </button>
      
      {showSuggestions && (suggestions.length > 0 || loading || error || aiLimitReached) && (
        <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 w-64">
          {loading ? (
            <div className="px-4 py-2 text-sm text-gray-500">Loading suggestions...</div>
          ) : error ? (
            <div className="px-4 py-2 text-sm text-red-500">{error}</div>
          ) : aiLimitReached ? (
            <div className="px-4 py-2 text-sm">
              <p className="text-red-500 mb-1">Daily AI suggestion limit reached.</p>
              <p className="text-gray-600 text-xs">Upgrade to premium for more suggestions.</p>
            </div>
          ) : suggestions.length > 0 ? (
            suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm text-gray-700"
                type="button" // Explicitly set type to button to prevent form submission
              >
                {suggestion}
              </button>
            ))
          ) : (
            <div className="px-4 py-2 text-sm text-gray-500">No suggestions available</div>
          )}
        </div>
      )}
    </div>
  );
}